import React, { FC, ReactNode } from "react";
import classnames from "classnames";
import { Button, ButtonExport } from "@shared/components";
import "./BulkActions.scss";

export type BulkActionLabel = string | { singular: string; plural: string };

export type BulkActionsComponentProps = {
  children?: ReactNode;
  className?: string;
};

export type BulkActionsProps = BulkActionsToolbarProps & { children: ReactNode };

export type BulkActionsToolbarProps<T = unknown> = {
  selected: T[];
  labels?: { items?: BulkActionLabel; download?: BulkActionLabel; deselect?: BulkActionLabel };
  onSelect: (selected: T[]) => void;
  onDownload?: (selected: T[]) => void;
};

const pluralize = (count: number, label: string | { singular: string; plural: string } = "items") => {
  if (typeof label === "string") {
    return label;
  }

  return count === 1 ? label.singular : label.plural;
};

export const BulkActions: FC<BulkActionsProps> = ({ children, selected, labels, onSelect, onDownload }) => {
  return (
    <BulkActionsContainer>
      {children}
      {selected?.length > 0 && (
        <BulkActionsToolbar selected={selected} labels={labels} onSelect={onSelect} onDownload={onDownload} />
      )}
    </BulkActionsContainer>
  );
};

export const BulkActionsToolbar: FC<BulkActionsToolbarProps> = ({ selected = [], labels, onSelect, onDownload }) => {
  return (
    <BulkActionsContent>
      <BulkActionsContentLeft>
        {selected.length} {pluralize(selected.length, labels?.items ?? "items")} selected{" "}
        {onSelect && (
          <Button buttonStyle="link" onClick={() => onSelect([])}>
            {pluralize(selected.length, labels?.deselect ?? "Deselect")}
          </Button>
        )}
      </BulkActionsContentLeft>

      <BulkActionsContentRight>
        {onDownload && (
          <ButtonExport onClick={() => onDownload(selected)}>
            {pluralize(selected.length, labels?.download ?? "Export")}
          </ButtonExport>
        )}
      </BulkActionsContentRight>
    </BulkActionsContent>
  );
};

export const BulkActionsContainer: FC<BulkActionsComponentProps> = ({ children }) => {
  return <div className={classnames("table-bulk-actions-container")}>{children}</div>;
};

export const BulkActionsContent: FC<BulkActionsComponentProps> = ({ children }) => {
  return <div className={classnames("table-bulk-actions-content")}>{children}</div>;
};

export const BulkActionsContentLeft: FC<BulkActionsComponentProps> = ({ children }) => {
  return <div className={classnames("table-bulk-actions-content-left")}>{children}</div>;
};

export const BulkActionsContentRight: FC<BulkActionsComponentProps> = ({ children }) => {
  return <div className={classnames("table-bulk-actions-content-right")}>{children}</div>;
};
